import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../../api/client';
import {
  requestSignupVerification,
  resendSignupVerification,
  setLocalCredential,
  verifySignupCode,
  SignupVerificationVerifyResponse,
} from '../../api/auth';
import { formatCountdown, useVerificationFlow } from '../../hooks/useVerificationFlow';

export type EmailCredentialModalProps = {
  onClose: () => void;
  /** 등록 성공 시 — 부모가 연동 상태를 갱신하고 모달을 닫는다. */
  onSuccess: () => void;
  open: boolean;
};

function ModalCloseIcon() {
  return (
    <svg aria-hidden fill="none" height="20" viewBox="0 0 20 20" width="20">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function credentialErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'AUTH-SOCIAL-008':
        return '이미 이메일 로그인이 설정된 계정입니다.';
      case 'AUTH-SIGNUP-001':
        return '이미 가입된 이메일입니다. 다른 이메일을 사용해주세요.';
      case 'AUTH-EMAIL-004':
      case 'AUTH-EMAIL-005':
        return '이메일 인증이 만료되었거나 유효하지 않습니다. 인증을 다시 진행해주세요.';
      default:
        return err.message;
    }
  }
  return '이메일 로그인 등록에 실패했습니다.';
}

/**
 * 소셜-온리 계정에 이메일/비밀번호 로그인 수단을 추가하는 모달.
 * 회원가입과 동일한 이메일 인증 체인(request/resend/verify)을 재사용한다.
 */
export function EmailCredentialModal({ onClose, onSuccess, open }: EmailCredentialModalProps) {
  const titleId = useId();
  const emailInputId = useId();
  const codeInputId = useId();
  const passwordInputId = useId();

  const {
    email,
    handleVerificationCodeChange,
    handleVerificationRequest,
    remainingSeconds,
    setEmail,
    toastMessage,
    verificationCode,
    verificationRequested,
    verificationStatus,
    verifiedResult,
  } = useVerificationFlow<SignupVerificationVerifyResponse>({
    requestVerification: requestSignupVerification,
    resendVerification: resendSignupVerification,
    verifyCode: verifySignupCode,
  });

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const reset = useCallback(() => {
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setSubmitting(false);
    setSubmitError('');
  }, [setEmail]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  const requesting = verificationStatus === 'requesting';
  const verifying = verificationStatus === 'verifying';
  const verified = verificationStatus === 'verified' && verifiedResult !== null;
  const passwordHasSpecialCharacter = /[^A-Za-z0-9]/.test(password);
  const passwordIsValid =
    password.length >= 8 && password.length <= 20 && passwordHasSpecialCharacter;
  const passwordsMatch = password.length > 0 && password === passwordConfirm;
  const canSubmit = verified && passwordIsValid && passwordsMatch && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !verifiedResult) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await setLocalCredential(verifiedResult.verifiedEmailToken, password);
      reset();
      onSuccess();
    } catch (err) {
      setSubmitError(credentialErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const visibleError = submitError || toastMessage;

  const dialog = (
    <div className="phone-verify-modal" role="presentation" onClick={handleClose}>
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="phone-verify-modal__panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header className="phone-verify-modal__header">
          <h2 className="phone-verify-modal__title" id={titleId}>
            이메일 로그인 등록
          </h2>
          <button
            aria-label="닫기"
            className="phone-verify-modal__close"
            onClick={handleClose}
            type="button"
          >
            <ModalCloseIcon />
          </button>
        </header>

        <div className="phone-verify-modal__body">
          <label className="phone-verify-modal__label" htmlFor={emailInputId}>
            이메일
          </label>
          <div className="phone-verify-modal__row">
            <input
              autoComplete="email"
              className="phone-verify-modal__input"
              id={emailInputId}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="이메일을 입력해주세요."
              type="email"
              value={email}
            />
            <button
              className="phone-verify-modal__action"
              disabled={email.trim().length === 0 || requesting || verified}
              onClick={handleVerificationRequest}
              type="button"
            >
              {requesting ? '전송 중...' : verificationRequested ? '재전송' : '인증코드 전송'}
            </button>
          </div>

          {verificationRequested && !verified ? (
            <>
              <label className="phone-verify-modal__label" htmlFor={codeInputId}>
                인증코드
              </label>
              <div className="phone-verify-modal__row">
                <input
                  autoComplete="one-time-code"
                  className="phone-verify-modal__input"
                  id={codeInputId}
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(event) => handleVerificationCodeChange(event.target.value)}
                  placeholder="6자리 인증코드"
                  value={verificationCode}
                />
              </div>
              <p className="phone-verify-modal__help">
                {verifying
                  ? '인증코드 확인 중...'
                  : `남은 시간 ${formatCountdown(remainingSeconds)} — 6자리를 입력하면 자동으로 확인됩니다.`}
              </p>
            </>
          ) : null}

          {verified ? (
            <>
              <p className="phone-verify-modal__help">
                이메일 인증이 완료되었습니다. 사용할 비밀번호를 입력해주세요.
              </p>
              <label className="phone-verify-modal__label" htmlFor={passwordInputId}>
                비밀번호
              </label>
              <input
                autoComplete="new-password"
                className="phone-verify-modal__input"
                id={passwordInputId}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="8~20자, 특수문자 포함"
                type="password"
                value={password}
              />
              <input
                aria-label="비밀번호 확인"
                autoComplete="new-password"
                className="phone-verify-modal__input"
                onChange={(event) => setPasswordConfirm(event.target.value)}
                placeholder="비밀번호를 재입력해주세요."
                type="password"
                value={passwordConfirm}
              />
            </>
          ) : null}

          {visibleError ? (
            <p className="phone-verify-modal__error" role="alert">
              {visibleError}
            </p>
          ) : null}
        </div>

        <footer className="phone-verify-modal__footer">
          <button
            className="phone-verify-modal__btn phone-verify-modal__btn--secondary"
            onClick={handleClose}
            type="button"
          >
            취소
          </button>
          {verified ? (
            <button
              className="phone-verify-modal__btn phone-verify-modal__btn--primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
              type="button"
            >
              {submitting ? '등록 중...' : '등록'}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
